import type { ApiRouteLabelGroup } from "griptonite-api-client";

import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Add, ChevronRight, DragIndicator, MoreHoriz } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dropdown,
  IconButton,
  ListDivider,
  Menu,
  MenuButton,
  MenuItem,
  Sheet,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useMemo, useRef, useState } from "react";

import {
  useBulkUpdateRouteLabelsMutation,
  useCreateRouteLabelMutation,
  useDeleteRouteLabelGroupMutation,
  useDeleteRouteLabelMutation,
  useUpdateRouteLabelGroupMutation,
  useUpdateRouteLabelMutation,
} from "@/hooks/routeLabels";

import InlineNameField from "./InlineNameField";
import LabelChip from "./LabelChip";
import { reorderIds, toOrderUpdates } from "./routeLabelsConfig";
import { dropIndicatorSx, ROWS_SCOPE, useSortableItem, useSortableMonitor } from "./sortable";
import VisibleToClimbersIcon from "./VisibleToClimbersIcon";

type LabelGroupRowProps = {
  // A label with options. The options are the group's active labels only.
  group: ApiRouteLabelGroup;
  expanded: boolean;
  onToggle: () => void;
  // Opens the group, for actions that need to show its labels.
  onExpand: () => void;
};

export default function LabelGroupRow({ group, expanded, onToggle, onExpand }: LabelGroupRowProps) {
  const updateGroup = useUpdateRouteLabelGroupMutation();
  const updateLabel = useUpdateRouteLabelMutation();
  const createLabel = useCreateRouteLabelMutation();
  const bulkUpdateLabels = useBulkUpdateRouteLabelsMutation();
  const deleteGroup = useDeleteRouteLabelGroupMutation();
  const deleteLabel = useDeleteRouteLabelMutation();

  const [isRenaming, setIsRenaming] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [renamingLabelUuid, setRenamingLabelUuid] = useState<string | null>(null);
  const [labelMenu, setLabelMenu] = useState<{ uuid: string; anchor: HTMLElement } | null>(null);
  // While set, the labels are in reorder mode and this is the order being edited.
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const isReordering = draftOrder !== null;

  const headerRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLDivElement>(null);
  const { ref, isDragging, edge } = useSortableItem<HTMLDivElement>({
    scope: ROWS_SCOPE,
    id: group.uuid,
    axis: "vertical",
    dragElementRef: headerRef,
    handleRef: gripRef,
    previewShape: "row",
  });

  const labelsScope = `labels:${group.uuid}`;
  useSortableMonitor(labelsScope, "horizontal", (sourceId, targetId, dropEdge) =>
    setDraftOrder((order) => order && reorderIds(order, sourceId, targetId, dropEdge)),
  );

  const labelsByUuid = useMemo(
    () => new Map(group.labels.map((label) => [label.uuid, label])),
    [group.labels],
  );
  const shownLabels = isReordering
    ? draftOrder.flatMap((uuid) => labelsByUuid.get(uuid) ?? [])
    : group.labels;

  const startReordering = () => {
    onExpand();
    setIsAdding(false);
    setDraftOrder(group.labels.map((label) => label.uuid));
  };

  const saveOrder = () => {
    const updates = toOrderUpdates(draftOrder ?? [], group.labels);
    setDraftOrder(null);
    if (updates.length > 0) {
      bulkUpdateLabels.mutate(updates);
    }
  };

  const menuLabel = labelMenu ? labelsByUuid.get(labelMenu.uuid) : undefined;

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
        ...dropIndicatorSx(edge, "vertical"),
      }}
    >
      <Stack
        ref={headerRef}
        direction="row"
        alignItems="center"
        gap={1.5}
        onClick={onToggle}
        sx={{
          minHeight: 52,
          pl: 1.25,
          pr: 1.5,
          cursor: "pointer",
          backgroundColor: expanded ? "background.level1" : undefined,
          "&:hover": { backgroundColor: "background.level1" },
        }}
      >
        <Box
          ref={gripRef}
          aria-hidden
          onClick={(event) => event.stopPropagation()}
          sx={{ display: "flex", color: "neutral.500", cursor: "grab" }}
        >
          <DragIndicator fontSize="small" />
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          aria-expanded={expanded}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${group.name}`}
          // Clicks bubble up to the header, which does the toggling.
          sx={{ "& svg": { transform: expanded ? "rotate(90deg)" : undefined } }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>

        {isRenaming ? (
          <Box onClick={(event) => event.stopPropagation()} sx={{ flex: 1 }}>
            <InlineNameField
              label={`Rename ${group.name}`}
              initialValue={group.name}
              width={260}
              onSave={(name) => updateGroup.mutateAsync({ uuid: group.uuid, name })}
              onClose={() => setIsRenaming(false)}
            />
          </Box>
        ) : (
          <>
            <Typography level="title-md" noWrap>
              {group.name}
            </Typography>
            <Typography
              level="body-sm"
              noWrap
              sx={{ flex: 1, minWidth: 0, visibility: expanded ? "hidden" : undefined }}
            >
              {group.labels.map((label) => label.name).join(", ")}
            </Typography>
          </>
        )}

        <Stack direction="row" alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
          {group.isPublic ? <VisibleToClimbersIcon /> : null}
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "plain",
                  color: "neutral",
                  "aria-label": `Options for ${group.name}`,
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </MenuButton>
            <Menu size="sm" placement="bottom-end" sx={{ minWidth: 220 }}>
              <MenuItem onClick={startReordering}>Reorder options</MenuItem>
              <MenuItem onClick={() => setIsRenaming(true)}>Rename label</MenuItem>
              <MenuItem
                role="menuitemcheckbox"
                aria-checked={group.isPublic}
                onClick={() => updateGroup.mutate({ uuid: group.uuid, isPublic: !group.isPublic })}
                sx={{ justifyContent: "space-between", gap: 2 }}
              >
                Visible to climbers
                <Switch
                  size="sm"
                  checked={group.isPublic}
                  // The menu item does the toggling.
                  onChange={() => undefined}
                  slotProps={{ input: { tabIndex: -1 } }}
                />
              </MenuItem>
              <ListDivider />
              {/* Labels on routes can only be archived, so those routes keep them. */}
              {group.routeCount === 0 ? (
                <MenuItem color="danger" onClick={() => deleteGroup.mutate(group.uuid)}>
                  Delete
                </MenuItem>
              ) : (
                <MenuItem
                  color="danger"
                  onClick={() => updateGroup.mutate({ uuid: group.uuid, isActive: false })}
                >
                  Archive label
                </MenuItem>
              )}
            </Menu>
          </Dropdown>
        </Stack>
      </Stack>

      {expanded ? (
        <Box
          sx={{
            p: 2,
            px: 1.5,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          {isReordering ? (
            <Sheet
              color="primary"
              variant="soft"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                py: 0.75,
                pl: 1.5,
                pr: 1,
                borderRadius: "sm",
              }}
            >
              <Typography level="body-sm" textColor="primary.700" sx={{ flex: 1 }}>
                Drag options to reorder them, then save.
              </Typography>
              <Button size="sm" variant="plain" onClick={() => setDraftOrder(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={saveOrder}>
                Save order
              </Button>
            </Sheet>
          ) : null}

          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
            {shownLabels.map((label) =>
              label.uuid === renamingLabelUuid ? (
                <InlineNameField
                  key={label.uuid}
                  label={`Rename ${label.name}`}
                  initialValue={label.name}
                  onSave={(name) => updateLabel.mutateAsync({ uuid: label.uuid, name })}
                  onClose={() => setRenamingLabelUuid(null)}
                />
              ) : (
                <LabelChip
                  key={label.uuid}
                  uuid={label.uuid}
                  name={label.name}
                  scope={labelsScope}
                  reordering={isReordering}
                  onClick={(anchor) => setLabelMenu({ uuid: label.uuid, anchor })}
                />
              ),
            )}
            {!isReordering ? (
              isAdding ? (
                <InlineNameField
                  label={`New option for ${group.name}`}
                  keepOpen
                  onSave={(name) => createLabel.mutateAsync({ name, group: group.uuid })}
                  onClose={() => setIsAdding(false)}
                />
              ) : (
                <Chip
                  size="lg"
                  variant="outlined"
                  color="primary"
                  startDecorator={<Add fontSize="small" />}
                  onClick={() => setIsAdding(true)}
                  sx={{ button: { borderStyle: "dashed", borderWidth: "2px" } }}
                >
                  Add option
                </Chip>
              )
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {labelMenu && menuLabel ? (
        <ClickAwayListener onClickAway={() => setLabelMenu(null)}>
          <Menu
            size="sm"
            open
            anchorEl={labelMenu.anchor}
            placement="bottom-start"
            onClose={() => setLabelMenu(null)}
          >
            <MenuItem
              onClick={() => {
                setRenamingLabelUuid(menuLabel.uuid);
                setLabelMenu(null);
              }}
            >
              Rename
            </MenuItem>
            <MenuItem
              color="danger"
              onClick={() => {
                if (menuLabel.routeCount === 0) {
                  deleteLabel.mutate(menuLabel.uuid);
                } else {
                  updateLabel.mutate({ uuid: menuLabel.uuid, isActive: false });
                }
                setLabelMenu(null);
              }}
            >
              {menuLabel.routeCount === 0 ? "Delete" : "Archive"}
            </MenuItem>
          </Menu>
        </ClickAwayListener>
      ) : null}
    </Box>
  );
}
